// ============================================================================
// Working subs + their insurance state — the list Antonela chases.
//
// ONE source: v_provider_compliance_status, which derives COI and W-9 state
// from partner_documents. Scope is fixed at the query: Active providers with
// active_assignments > 0 — a sub with no live work is not an exposure.
//
// ⛔ 'No COI on file' is the LARGER population and it is invisible to every
// alert path we have, because a detector that compares expiry dates cannot fire
// on a provider that has none. It is ordered and styled level with 'Expired'
// here on purpose. Do not "simplify" that away.
//
// Flat-fetch + IN-lookup + client-join, house style. No TanStack Query.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';
// ⛔ This file used to keep its own list of the three spellings of the main
// cleaner, written out by hand because nothing in the system could answer "is
// this the main cleaner". One place answers it now.
import { isMainClean } from '../../../shared/service-roles';

export interface WorkingSubInsuranceRow {
  provider_id: string;
  /** legal_business_name falling back to partner_name, from the view. */
  display_name: string;
  /** Owner or main cleaner. AMs know these subs by person, not business name. */
  person: string | null;
  market_region: string | null;
  coi_state: string;
  coi_severity: number;
  coi_expiration_date: string | null;
  /** 0 = never uploaded. >0 with state 'No COI on file' = uploaded WITHOUT a date. */
  coi_doc_count: number;
  w9_state: string | null;
  active_assignments: number;
}

export function useWorkingSubInsurance(): {
  rows: WorkingSubInsuranceRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [rows, setRows] = useState<WorkingSubInsuranceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: comp, error: e1 } = await supabase
          .from('v_provider_compliance_status')
          .select(
            'provider_id, display_name, partner_name, market_region, coi_state, coi_severity, coi_expiration_date, coi_doc_count, w9_state, active_assignments',
          )
          .eq('provider_status', 'Active')
          .gt('active_assignments', 0)
          .order('coi_severity', { ascending: true })
          .order('coi_expiration_date', { ascending: true, nullsFirst: true });
        if (e1) throw e1;
        const base = (comp ?? []) as (Omit<WorkingSubInsuranceRow, 'person'> & {
          partner_name: string | null;
        })[];
        const ids = base.map((r) => r.provider_id);

        // The person, resolved from the two places one can live. owner_name
        // wins; otherwise the primary cleaner on an active assignment.
        const [ownerRes, asgRes] = ids.length
          ? await Promise.all([
              supabase.from('service_providers').select('provider_id, owner_name').in('provider_id', ids),
              supabase
                .from('service_assignments')
                .select('provider_id, worker_name, service_code, assignment_id')
                .in('provider_id', ids)
                .eq('assignment_status', 'Active'),
            ])
          : [
              { data: [], error: null },
              { data: [], error: null },
            ];

        const ownerBy: Record<string, string | null> = {};
        for (const o of (ownerRes.data ?? []) as {
          provider_id: string;
          owner_name: string | null;
        }[]) {
          ownerBy[o.provider_id] = o.owner_name;
        }

        const cleanerBy: Record<string, { name: string; primary: boolean; id: string }> = {};
        for (const a of (asgRes.data ?? []) as {
          provider_id: string | null;
          worker_name: string | null;
          service_code: string | null;
          assignment_id: string;
        }[]) {
          const name = a.worker_name?.trim();
          if (!a.provider_id || !name) continue;
          const isPrimary = isMainClean(a.service_code);
          const held = cleanerBy[a.provider_id];
          // Primary beats non-primary; within a tier, lowest assignment_id wins
          // so the label is stable between loads.
          if (!held || (isPrimary && !held.primary) || (isPrimary === held.primary && a.assignment_id < held.id)) {
            cleanerBy[a.provider_id] = { name, primary: isPrimary, id: a.assignment_id };
          }
        }

        const built: WorkingSubInsuranceRow[] = base.map((r) => {
          const display = r.display_name ?? r.partner_name ?? r.provider_id;
          const owner = ownerBy[r.provider_id]?.trim() || null;
          const cleaner = cleanerBy[r.provider_id]?.name ?? null;
          const candidate = owner ?? cleaner;
          return {
            provider_id: r.provider_id,
            display_name: display,
            // Only worth showing when it says something the name does not.
            person:
              candidate && candidate.toLowerCase() !== display.toLowerCase() ? candidate : null,
            market_region: r.market_region,
            coi_state: r.coi_state,
            coi_severity: r.coi_severity,
            coi_expiration_date: r.coi_expiration_date,
            coi_doc_count: r.coi_doc_count ?? 0,
            w9_state: r.w9_state,
            active_assignments: r.active_assignments ?? 0,
          };
        });
        if (cancelled) return;
        setRows(built);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load subcontractor insurance status.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { rows, loading, error, refetch: useCallback(() => setTick((n) => n + 1), []) };
}
